import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  ExternalLink,
  Key,
  Lock,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';

interface OAuthAuthorizationPromptProps {
  tool: string;
  action: string;
  authUrl: string;
  message: string;
  onCodeSubmit: (code: string) => void;
  onCancel?: () => void;
  actionRequired?: string; // 'oauth_authorization' or 'connect_oauth'
}

/**
 * OAuthAuthorizationPrompt Component
 *
 * Displays OAuth authorization prompts when MCP tools require user authorization
 * Features:
 * - Clickable authorization URL button
 * - Authorization code input
 * - Clear instructions for OAuth flow
 * - Error handling
 */
export const OAuthAuthorizationPrompt: React.FC<OAuthAuthorizationPromptProps> = ({
  tool,
  action,
  authUrl,
  message,
  onCodeSubmit,
  onCancel,
  actionRequired = 'oauth_authorization',
}) => {
  const [authCode, setAuthCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasOpenedAuth, setHasOpenedAuth] = useState(false);

  // Determine if this is the simpler per-user OAuth flow
  const isPerUserOAuth = actionRequired === 'connect_oauth';

  const handleOpenAuth = () => {
    window.open(authUrl, '_blank', 'width=600,height=700');
    setHasOpenedAuth(true);
  };

  const handleSubmitCode = () => {
    if (!authCode.trim()) return;

    setIsSubmitting(true);
    onCodeSubmit(authCode.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && authCode.trim() && !isSubmitting) {
      handleSubmitCode();
    }
  };

  return (
    <Card className="p-4 border-blue-200 bg-blue-50">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Lock className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-blue-900">Authorization Required</h4>
            <Badge variant="outline" className="text-xs">
              {tool}
            </Badge>
          </div>
          <p className="text-sm text-blue-800">{message}</p>
        </div>
      </div>

      {/* Authorization Steps */}
      {isPerUserOAuth ? (
        /* Per-User OAuth Flow - Single Click */
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 py-2">
            <p className="text-sm text-blue-900 text-center">
              Click the button below to connect your {tool} account:
            </p>
            <Button
              onClick={handleOpenAuth}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              size="lg"
            >
              <ExternalLink className="h-5 w-5 mr-2" />
              Connect {tool}
            </Button>
            {hasOpenedAuth && (
              <Alert className="border-green-200 bg-green-50 mt-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-900 text-sm">
                  Authorization window opened. After connecting, return here and retry your request.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      ) : (
        /* Code-based OAuth Flow - Two Steps */
        <div className="space-y-4">
          {/* Step 1: Authorize */}
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0 mt-0.5">
              1
            </div>
            <div className="flex-1">
              <p className="text-sm text-blue-900 mb-2">
                Click the button below to authorize {tool}:
              </p>
              <Button
                onClick={handleOpenAuth}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Authorize {tool}
              </Button>
              {hasOpenedAuth && (
                <div className="flex items-center gap-1 mt-2 text-xs text-green-700">
                  <CheckCircle className="h-3 w-3" />
                  Authorization window opened
                </div>
              )}
            </div>
          </div>

          {/* Step 2: Paste Code */}
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0 mt-0.5">
              2
            </div>
            <div className="flex-1">
              <p className="text-sm text-blue-900 mb-2">
                After authorizing, paste the authorization code here:
              </p>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-400" />
                  <Input
                    placeholder="Paste authorization code..."
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isSubmitting}
                    className="pl-10 bg-white border-blue-200 focus:border-blue-400"
                  />
                </div>
                <Button
                  onClick={handleSubmitCode}
                  disabled={!authCode.trim() || isSubmitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Submit
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      {!isPerUserOAuth && (
        <Alert className="mt-4 border-blue-200 bg-blue-100">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-xs text-blue-800">
            <strong>Note:</strong> The authorization code is typically displayed after you
            complete the authorization process. Copy it and paste it above.
          </AlertDescription>
        </Alert>
      )}

      {/* Cancel Button */}
      {onCancel && (
        <div className="mt-4 pt-4 border-t border-blue-200">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
          >
            Cancel Authorization
          </Button>
        </div>
      )}
    </Card>
  );
};
